import React, { useState, useMemo } from 'react'
import { Box, Typography, Stack, Chip, useTheme } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SpeedIcon from '@mui/icons-material/Speed'
import SavingsIcon from '@mui/icons-material/Savings'
import { generateInsights } from '../utils/insightsEngine'

// Severity → color / label
const SEVERITY = {
  high:   { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',    label: 'High'   },
  medium: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',   label: 'Medium' },
  low:    { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',   label: 'Low'    },
}

// Health score → color / label
function healthConfig(score) {
  if (score >= 80) return { color: '#10b981', label: 'Healthy'  }
  if (score >= 60) return { color: '#f59e0b', label: 'Fair'     }
  if (score >= 40) return { color: '#ef4444', label: 'At Risk'  }
  return              { color: '#dc2626', label: 'Critical' }
}

// SVG circular progress arc
function ScoreRing({ score, color, size = 80, stroke = 8 }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const r   = (size - stroke) / 2
  const cx  = size / 2
  const circ = 2 * Math.PI * r
  const dash  = (score / 100) * circ
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'} strokeWidth={stroke} />
        <circle
          cx={cx} cy={cx} r={r} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.4,0,0.2,1)' }}
        />
      </svg>
      <Typography sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '20px', fontWeight: 900, color, lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {score}
      </Typography>
    </Box>
  )
}

// Collapsible section
function Section({ title, icon: Icon, accent, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const textPrimary   = isDark ? '#f1f5f9' : '#0f172a'
  const textSecondary = isDark ? '#64748b' : '#94a3b8'
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          px: 1.25, py: 0.875,
          borderRadius: '10px',
          cursor: 'pointer',
          transition: 'background 0.15s ease',
          '&:hover': { bgcolor: hoverBg },
        }}
      >
        <Box sx={{
          width: 28, height: 28, borderRadius: '8px',
          bgcolor: `${accent}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 15, color: accent }} />
        </Box>
        <Typography sx={{ flex: 1, fontSize: '13px', fontWeight: 700, color: textPrimary }}>
          {title}
        </Typography>
        <Chip
          label={count}
          size="small"
          sx={{
            height: 18, fontSize: '11px', fontWeight: 700,
            bgcolor: `${accent}18`, color: accent,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <ExpandMoreIcon sx={{
          fontSize: 17, color: textSecondary,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s ease',
        }} />
      </Box>

      {open && (
        <Box sx={{ pl: 1, pr: 0.5, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.875 }}>
          {children}
        </Box>
      )}
    </Box>
  )
}

// Individual insight card
function InsightCard({ item, accentColor }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const sev = SEVERITY[item.severity] || SEVERITY.low
  const textPrimary   = isDark ? '#f1f5f9' : '#0f172a'
  const textSecondary = isDark ? '#64748b' : '#6b7280'
  const surfaceEl     = isDark ? '#1a2a40' : '#ffffff'
  const border        = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <Box sx={{
      p: 1.5,
      borderRadius: '10px',
      bgcolor: surfaceEl,
      border: `1px solid ${border}`,
      borderLeft: `3px solid ${accentColor || sev.color}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: textPrimary, flex: 1, lineHeight: 1.3 }}>
          {item.title}
        </Typography>
        {item.severity && (
          <Chip
            label={sev.label}
            size="small"
            sx={{ height: 16, fontSize: '10px', fontWeight: 700, bgcolor: sev.bg, color: sev.color, '& .MuiChip-label': { px: 0.625 } }}
          />
        )}
      </Stack>

      <Typography sx={{ fontSize: '11.5px', color: textSecondary, lineHeight: 1.55 }}>
        {item.description}
      </Typography>

      {item.recommendation && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.625, mt: 0.375 }}>
          <LightbulbOutlinedIcon sx={{ fontSize: 12, color: '#f59e0b', mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '11px', color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.5, fontStyle: 'italic' }}>
            {item.recommendation}
          </Typography>
        </Box>
      )}

      {item.potentialSavings > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Chip
            icon={<SavingsIcon sx={{ fontSize: '11px !important' }} />}
            label={`Save $${item.potentialSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            size="small"
            sx={{ height: 20, fontSize: '11px', fontWeight: 700, bgcolor: 'rgba(16,185,129,0.12)', color: '#10b981', '& .MuiChip-label': { px: 0.75 } }}
          />
        </Box>
      )}
    </Box>
  )
}

// Key metric row
function MetricRow({ metric }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const textPrimary   = isDark ? '#f1f5f9' : '#0f172a'
  const textSecondary = isDark ? '#64748b' : '#6b7280'
  const surfaceEl     = isDark ? '#1a2a40' : '#ffffff'
  const border        = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      px: 1.5, py: 1, borderRadius: '9px',
      bgcolor: surfaceEl, border: `1px solid ${border}`,
    }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: textSecondary }}>
        {metric.metric}
      </Typography>
      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: textPrimary, fontVariantNumeric: 'tabular-nums' }}>
        {metric.value}
      </Typography>
    </Box>
  )
}

// ── Main component ─────────────────────────────────────────────────
function InsightsPanel({ simulationResults, nodes, edges, demands }) {
  const theme  = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const insights = useMemo(() => {
    if (!simulationResults) return null
    return generateInsights(simulationResults, nodes, edges, demands)
  }, [simulationResults, nodes, edges, demands])

  if (!insights) return null

  const textPrimary   = isDark ? '#f1f5f9' : '#0f172a'
  const textSecondary = isDark ? '#64748b' : '#94a3b8'
  const surface       = isDark ? '#0d1626' : '#eef2f7'
  const surfaceEl     = isDark ? '#1a2a40' : '#ffffff'
  const border        = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.09)'

  const { score, color: hColor, label: hLabel } = {
    score: insights.summary.overallHealth.score,
    color: healthConfig(insights.summary.overallHealth.score).color,
    label: healthConfig(insights.summary.overallHealth.score).label,
  }
  const scoreColor = healthConfig(score).color
  const scoreLabel = healthConfig(score).label

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* Health Score Card */}
      <Box sx={{
        p: 2,
        borderRadius: '14px',
        bgcolor: surfaceEl,
        border: `1px solid ${border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
      }}>
        <ScoreRing score={score} color={scoreColor} size={76} stroke={7} />

        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: scoreColor, lineHeight: 1 }}>
              {scoreLabel}
            </Typography>
            <Chip
              icon={<SpeedIcon sx={{ fontSize: '11px !important', color: `${textSecondary} !important` }} />}
              label="Network Health"
              size="small"
              sx={{ height: 18, fontSize: '10px', fontWeight: 600, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: textSecondary, '& .MuiChip-label': { px: 0.625 } }}
            />
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={1.25}>
            {insights.summary.totalWarnings > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Typography sx={{ fontSize: '11px', color: textSecondary }}>
                  {insights.summary.totalWarnings} warning{insights.summary.totalWarnings !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            {insights.summary.totalOptimizations > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
                <Typography sx={{ fontSize: '11px', color: textSecondary }}>
                  {insights.summary.totalOptimizations} optimization{insights.summary.totalOptimizations !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            {insights.summary.totalRecommendations > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                <Typography sx={{ fontSize: '11px', color: textSecondary }}>
                  {insights.summary.totalRecommendations} recommendation{insights.summary.totalRecommendations !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            {insights.summary.potentialSavings > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SavingsIcon sx={{ fontSize: 11, color: '#10b981' }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#10b981' }}>
                  ${insights.summary.potentialSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })} potential savings
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Warnings */}
      {insights.warnings.length > 0 && (
        <Section
          title="Warnings"
          icon={WarningAmberIcon}
          accent="#ef4444"
          count={insights.warnings.length}
          defaultOpen={true}
        >
          {insights.warnings.map((w, i) => (
            <InsightCard key={i} item={w} accentColor={SEVERITY[w.severity]?.color || '#ef4444'} />
          ))}
        </Section>
      )}

      {/* Optimizations */}
      {insights.optimizations.length > 0 && (
        <Section
          title="Optimizations"
          icon={TrendingUpIcon}
          accent="#10b981"
          count={insights.optimizations.length}
          defaultOpen={true}
        >
          {insights.optimizations.map((o, i) => (
            <InsightCard key={i} item={o} accentColor="#10b981" />
          ))}
        </Section>
      )}

      {/* Recommendations */}
      {insights.recommendations.length > 0 && (
        <Section
          title="Recommendations"
          icon={InfoOutlinedIcon}
          accent="#3b82f6"
          count={insights.recommendations.length}
        >
          {insights.recommendations.map((r, i) => (
            <InsightCard key={i} item={r} accentColor="#3b82f6" />
          ))}
        </Section>
      )}

      {/* Key Metrics */}
      {insights.keyMetrics.length > 0 && (
        <Section
          title="Key Metrics"
          icon={CheckCircleOutlineIcon}
          accent="#8b5cf6"
          count={insights.keyMetrics.length}
        >
          {insights.keyMetrics.map((m, i) => (
            <MetricRow key={i} metric={m} />
          ))}
        </Section>
      )}

    </Box>
  )
}

export default InsightsPanel
